import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { Login } from "@/components/auth/Login";
import { ProjectHeader } from "@/components/projects/ProjectHeader";
import { ProjectActions } from "@/components/projects/ProjectActions";
import { SharedProjectsList } from "@/components/share/SharedProjectsList";
import { ProjectCalendarOverview } from "@/components/projects/ProjectCalendarOverview";
import { Project } from "@/types/project";
import { OnboardingDialog } from "@/components/onboarding/OnboardingDialog";
import { ProjectList } from "@/components/projects/ProjectList";
import { ProjectDialogs } from "@/components/projects/ProjectDialogs";
import { Profile } from "@/types/profile";
import { Footer } from "@/components/layout/Footer";

export default function Index() {
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [duplicatingProject, setDuplicatingProject] = useState<Project | null>(null);
  const [selectedProjectForSharing, setSelectedProjectForSharing] = useState<Project | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: profile, isLoading: isProfileLoading } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error) throw error;

      const transformedProfile: Profile = {
        ...data,
        subscription_tier: data.subscription_tier as 'free' | 'pro' | 'producer' | null,
        display_preferences: data.display_preferences ? JSON.parse(JSON.stringify(data.display_preferences)) : {
          theme: "system",
          fontSize: "normal",
          highContrast: false
        },
        notification_preferences: data.notification_preferences ? JSON.parse(JSON.stringify(data.notification_preferences)) : {
          emailNotifications: true,
          inAppNotifications: true,
          collaborationAlerts: true
        }
      };

      return transformedProfile;
    },
    enabled: !!user,
  });

  const { data: projects, refetch } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq('user_id', user.id)
        .order("created_at", { ascending: false });

      if (error) {
        toast.error("Failed to load projects");
        throw error;
      }

      const transformedProjects: Project[] = data.map(project => ({
        ...project,
        scout_photos: Array.isArray(project.scout_photos) 
          ? project.scout_photos.map(String)
          : [],
        documents: Array.isArray(project.documents)
          ? project.documents.map((doc: any) => ({
              name: typeof doc === 'object' && doc !== null && 'name' in doc ? String(doc.name) : '',
              url: typeof doc === 'object' && doc !== null && 'url' in doc ? String(doc.url) : ''
            }))
          : [],
        custom_categories: Array.isArray(project.custom_categories)
          ? project.custom_categories.map(String)
          : [],
        shoot_dates: Array.isArray(project.shoot_dates)
          ? project.shoot_dates.map(String)
          : [],
        archived_at: project.archived_at ? String(project.archived_at) : null,
        camera_enabled: project.camera_enabled ?? true,
        ge_enabled: project.ge_enabled ?? true
      }));

      return transformedProjects;
    },
    enabled: !!user,
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });

  const { data: sharedProjects } = useQuery({
    queryKey: ["shared-projects"],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from("project_shares")
        .select(`
          *,
          projects:project_id (
            name,
            description
          ),
          shared_by_profile:profiles!project_shares_shared_by_fkey (
            full_name
          )
        `)
        .eq("shared_with", user.id);

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const handleArchiveProject = async (projectId: string) => {
    const project = projects?.find(p => p.id === projectId);
    if (!project) {
      toast.error("Project not found");
      return;
    }

    const isArchived = !!project.archived_at;
    
    try {
      const { error } = await supabase
        .from('projects')
        .update({
          archived_at: isArchived ? null : new Date().toISOString(),
        })
        .eq('id', projectId);

      if (error) {
        console.error("Error archiving project:", error);
        throw error;
      }

      toast.success(isArchived ? "Project unarchived successfully!" : "Project archived successfully!");
      await refetch();
    } catch (error: any) {
      console.error("Error archiving project:", error);
      toast.error(`Failed to ${isArchived ? 'unarchive' : 'archive'} project: ${error.message || 'Unknown error'}`);
      throw error;
    }
  };

  const handleDuplicateProject = (project: Project) => {
    setDuplicatingProject(project);
    setShowProjectForm(true);
  };

  const handleCreateProject = async (project: Project) => {
    if (!user) {
      toast.error("You must be logged in to create a project");
      return;
    }

    const isPaidUser = profile?.subscription_tier === 'pro' || profile?.subscription_tier === 'producer';
    if (!isPaidUser && projects && projects.filter(p => !p.archived_at).length > 0) {
      toast.error(
        "During our beta period, free users are limited to one active project at a time. " +
        "Please upgrade to pro for unlimited projects, or archive your current project before creating a new one."
      );
      setShowProjectForm(false);
      return;
    }

    try {
      // Prepare the project data - remove ID if it exists to let database generate new one
      const projectData = {
        name: project.name,
        description: project.description,
        camera_budget: project.camera_budget,
        ge_budget: project.ge_budget,
        budget_enabled: project.budget_enabled,
        camera_enabled: project.camera_enabled,
        ge_enabled: project.ge_enabled,
        prep_date: project.prep_date,
        pre_light_date: project.pre_light_date,
        shoot_dates: project.shoot_dates || [],
        custom_categories: project.custom_categories || [],
        user_id: user.id,
        camera_spent: 0,
        ge_spent: 0,
        status: 'active',
        cost_tracking_only: project.cost_tracking_only || false,
        camera_notes: project.camera_notes || null,
        ge_notes: project.ge_notes || null,
        scout_photos: [],
        documents: [],
        general_notes: project.general_notes || null
      };

      const { data: newProject, error: projectError } = await supabase
        .from("projects")
        .insert([projectData])
        .select()
        .single();

      if (projectError) throw projectError;

      // If this is a duplicate project, copy the gear items from the original
      if (duplicatingProject && duplicatingProject.id) {
        console.log('Duplicating gear from project:', duplicatingProject.id);
        const { data: originalGearItems, error: gearFetchError } = await supabase
          .from("gear_items")
          .select("*")
          .eq("project_id", duplicatingProject.id);

        console.log('Original gear items found:', originalGearItems?.length || 0);

        if (gearFetchError) {
          console.error("Failed to fetch original gear items:", gearFetchError);
          toast.error("Project created but failed to fetch original gear items");
        } else if (originalGearItems && originalGearItems.length > 0) {
          // Prepare gear items for the new project
          const gearItemsToInsert = originalGearItems.map(item => ({
            name: item.name,
            category: item.category,
            quantity: item.quantity,
            cost_per_unit: item.cost_per_unit,
            notes: item.notes,
            vendor_id: item.vendor_id,
            days_needed: item.days_needed,
            project_id: newProject.id
          }));

          console.log('Inserting gear items:', gearItemsToInsert.length);

          const { error: gearInsertError } = await supabase
            .from("gear_items")
            .insert(gearItemsToInsert);

          if (gearInsertError) {
            console.error("Failed to duplicate gear items:", gearInsertError);
            toast.error("Project created but failed to copy gear items");
          } else {
            toast.success(`Project duplicated successfully with ${originalGearItems.length} gear items!`);
          }
        } else {
          toast.success("Project duplicated successfully (no gear items to copy)!");
        }
      } else {
        toast.success("Project created successfully!");
      }
      
      refetch();
      setShowProjectForm(false);
      setDuplicatingProject(null);
    } catch (error: any) {
      console.error("Error creating project:", error);
      toast.error("Failed to create project");
    }
  };

  const handleEditProject = async (project: Project) => {
    try {
      const { error } = await supabase
        .from('projects')
        .update({
          name: project.name,
          description: project.description,
          camera_budget: project.camera_budget,
          ge_budget: project.ge_budget,
          budget_enabled: project.budget_enabled,
          prep_date: project.prep_date,
          pre_light_date: project.pre_light_date,
          shoot_dates: project.shoot_dates || [],
          custom_categories: project.custom_categories || [],
          scout_photos: project.scout_photos || [],
          camera_enabled: project.camera_enabled,
          ge_enabled: project.ge_enabled,
          camera_notes: project.camera_notes,
          ge_notes: project.ge_notes,
          cost_tracking_only: project.cost_tracking_only,
          general_notes: project.general_notes,
          documents: project.documents,
          updated_at: new Date().toISOString()
        })
        .eq('id', project.id);

      if (error) {
        console.error("Error updating project:", error);
        toast.error("Failed to update project: " + error.message);
        return;
      }

      toast.success("Project updated successfully");
      await refetch();
      setEditingProject(null);
    } catch (error: any) {
      console.error("Error updating project:", error);
      toast.error("Failed to update project: " + (error.message || "Unknown error"));
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      const { error: sharesError } = await supabase
        .from("project_shares")
        .delete()
        .eq("project_id", projectId);

      if (sharesError) throw sharesError;

      const { error: gearError } = await supabase
        .from("gear_items")
        .delete()
        .eq("project_id", projectId);

      if (gearError) throw gearError;

      const { error: projectError } = await supabase
        .from("projects")
        .delete()
        .eq("id", projectId);

      if (projectError) throw projectError;

      toast.success("Project deleted successfully!");
      refetch();
    } catch (error: any) {
      console.error("Error in project deletion process:", error);
      toast.error(`Failed to delete project: ${error.message}`);
    }
  };

  const handleCloseForm = () => {
    setShowProjectForm(false);
    setEditingProject(null);
    setDuplicatingProject(null);
  };

  if (!user) {
    return (
      <div className="min-h-screen w-full site-gradient relative overflow-hidden">
        <Login />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full site-gradient relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('data:image/png;base64,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')] opacity-[0.025]"></div>
      
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-purple-900/10 to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-purple-900/10 to-transparent"></div>
      
      {!isProfileLoading && user && profile && !profile.onboarding_completed && <OnboardingDialog />}
      
      <div className="container mx-auto px-4 py-6 relative z-10">
        <div className="max-w-7xl mx-auto space-y-6">
          <ProjectHeader />
          
          <ProjectActions onNewProject={() => setShowProjectForm(true)} />
          
          <div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-6">
            <div className="space-y-6">
              <ProjectList
                projects={projects || []}
                profile={profile}
                onNavigate={(id) => navigate(`/project/${id}`)}
                onShare={setSelectedProjectForSharing}
                onDuplicate={handleDuplicateProject}
                onEdit={setEditingProject}
                onDelete={handleDeleteProject}
                onArchive={handleArchiveProject}
                onNewProject={() => setShowProjectForm(true)}
              />

              {sharedProjects && sharedProjects.length > 0 && (
                <div className="glass-card p-6 rounded-xl border border-white/5 shadow-lg">
                  <SharedProjectsList />
                </div>
              )}
            </div>

            <div className="glass-card p-6 rounded-xl border border-white/5 shadow-lg backdrop-blur-md">
              <ProjectCalendarOverview />
            </div>
          </div>
        </div>
      </div>

      <ProjectDialogs
        selectedProjectForSharing={selectedProjectForSharing}
        showProjectForm={showProjectForm}
        editingProject={editingProject}
        duplicatingProject={duplicatingProject}
        onCloseShare={() => setSelectedProjectForSharing(null)}
        onCloseForm={handleCloseForm}
        onSubmit={editingProject ? handleEditProject : handleCreateProject}
      />

      <Footer />
    </div>
  );
}
